import React, { useState, useEffect, useCallback } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils';
import PreLoader from '../../../../core/common/preLoader';
import AddCourse from './courseAdd';
import ModifyCourse from './courseModify';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import DeleteModal from '../../../../core/modals/deleteModal';
import CustomToast from '../../../../core/common/customToast';
import CourseReviews from './courseReviews';
import "../../../../style/css/shared.scss";

const Courses = () => {
  const { hasAcademicMgntCreate, hasAcademicMgntDelete, hasAcademicMgntEdit } = useAccess();
  const { isLoading, data, callFetch } = useFetch();
  const [courses, setCourses] = useState<any[]>([]);
  const [editId, setEditId] = useState<number>(0);
  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  });
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedCourseForReviews, setSelectedCourseForReviews] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>(searchParams.get('searchText') || '');

  useEffect(() => {
    setSearchText(searchParams.get('searchText') || '');
  }, [searchParams]);

  const navigation = useNavigate();

  const fetchCourses = useCallback(() => {
    callFetch(
      API.COURSE,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).catch((err) => {});;
  }, []);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (data) {
      setCourses(data);
    }
  }, [data]);

  const onAddSubscription = (id: number) => {
    navigation(`${all_routes.subscriptions}?courseId=${id}&action=add`);
  };

  const onViewReviews = (course: any) => {
    setSelectedCourseForReviews(course);
    setShowReviewsModal(true);
  };

  const deleteData = (id: number) => {
    callFetch(
      `${API.COURSE}/${id}`,
      'DELETE',
      getAPIHeaders(),
      null,
      null
    )
      .then(() => {
        fetchCourses();
      })
      .catch((error) => {
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message: error?.response?.data || "Something went wrong, please try again later.",
        }));
      });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a.id - b.id,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Course",
      dataIndex: "title",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
            <span className="text-primary fw-bold">
              {record.title.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-semibold course-title">
              <Link 
                to="#" 
                className="text-reset text-decoration-none"
                onClick={() => { setEditId(record?.id) }}
                data-bs-toggle="modal"
                data-bs-target="#edit_course"
              >
                {record.title}
              </Link>
            </h6>
            <div className="d-flex align-items-center mt-1 course-info">
              <span className="badge badge-soft-primary me-2">{record.code}</span>
              {record.description && (
                <span className="fs-12 text-muted description-content text-truncate" 
                      style={{ maxWidth: '250px' }} 
                      title={record.description}>
                  {record.description}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title?.localeCompare(b.title),
      defaultSortOrder: 'ascend',
      width: "52%",
    },
    {
      title: "Subjects",
      dataIndex: "",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center flex-column">
          {record?.subjects?.length ? record?.subjects?.map((subjects:any) => (
            <div key={subjects.code}>
              <h6 className="mb-1 fw-medium syllabus-title">
                <Link 
                  to={`/academics/subjects?searchText=${subjects?.code}`}
                  className="text-reset text-decoration-none"
                  style={{ cursor: 'pointer' }}
                >
                  {subjects?.title}
                  {' '}
                  <span className="badge badge-soft-info me-2">{subjects?.code}</span>
                </Link>
              </h6>
            </div>
          )) : (
            <span className="text-muted">No subjects assigned</span>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => {
        const titleA = a?.syllabus && a?.syllabus[0] ? a.syllabus[0].title : '';
        const titleB = b?.syllabus && b?.syllabus[0] ? b.syllabus[0].title : '';
        return titleA.localeCompare(titleB);
      },
      width: "42%",
    },
    {
      title: "Category",
      dataIndex: "type",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center justify-content-center">
          <span className="badge badge-soft-info fs-12">
            {text || 'N/A'}
          </span>
        </div>
      ),
      sorter: (a: any, b: any) => a.type?.localeCompare(b.type),
      width: "25%",
      align: "center" as "center",
    },
    {
      title: "Online Course",
      dataIndex: "isCommercial",
      render: (text: string, record: any) => (
        <div className="action-icon d-flex justify-content-center">
          <span className={`badge badge-soft-${record.isCommercial ? 'primary' : 'secondary'} me-2`}>{record.isCommercial ? 'Yes' : 'No'}</span>
        </div>
      ),
      width: "15%",
      align: "center" as "center",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-flex justify-content-start align-items-center gap-2">
          <Link
            hidden={!hasAcademicMgntEdit()}
            to="#"
            className="icon-edit"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#edit_course"
            onClick={() => {
              setEditId(record?.id);
            }}
            title="Edit Course"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            hidden={!hasAcademicMgntDelete()}
            to="#"
            className="icon-delete"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() =>
              setDeleteItem({
                id: record.id,
                message: `Are you sure you want to delete ${record.title}? This can't be undone.`,
              })
            }
            title="Delete Course"
          >
            <i className="ti ti-trash" />
          </Link>
          {record?.isCommercial && (
            <>
              <button 
                hidden={!hasAcademicMgntDelete()}
                className="btn btn-outline-primary btn-sm" 
                onClick={() => onAddSubscription(record?.id)}
              >
                <i className="ti ti-briefcase" /> Add Subscription
              </button>
              <button 
                className="btn btn-outline-info btn-sm" 
                onClick={() => onViewReviews(record)}
                title="View Reviews"
              >
                <i className="ti ti-star" /> Reviews
              </button>
            </>
          )}
        </div>
      ),
      width: "20%",
      align: "start" as "start",
    },
  ];

  const renderAddCourse = () => {
    return <AddCourse onSuccess={fetchCourses} />;
  };

  const renderEditCourse = () => {
    return (
      <ModifyCourse
        onSuccess={fetchCourses}
        course={courses?.find((el: any) => el.id === editId)}
      />
    );
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deleteData(deleteItem?.id)}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Courses</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Academics</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Courses
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="d-flex mb-2 right-content gap-3">
                <button
                  disabled={!hasAcademicMgntCreate()}
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_course"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Course
                </button>
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    navigation(`${all_routes.courseAcademicWiz}`);
                  }}
                >
                  <i className="ti ti-settings me-2" />
                  Manage Courses
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Course List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={courses}
                  columns={columns}
                  Selection={false}
                  refresh={fetchCourses}
                  defaultSearchText={searchText}
                />
              ) : (
                <div className="course-cards p-3">
                  <div className="row">
                    {courses.map((course: any) => (
                      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={course.id}>
                        <div className="card h-100 border shadow-hover transition-all">
                          <div className="card-body position-relative">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {course.title.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold course-title">
                                  <Link 
                                    to="#" 
                                    className="text-reset text-decoration-none"
                                    onClick={() => { setEditId(course?.id) }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_course"
                                  >
                                    {course.title}
                                  </Link>
                                </h6>
                              </div>
                              <span className="badge badge-soft-primary">{course.code}</span>
                            </div>
                            
                            {course.description && (
                              <p className="fs-12 text-muted mb-3">
                                {course.description.length > 100 
                                  ? course.description.substring(0, 100) + '...' 
                                  : course.description
                                }
                              </p>
                            )}
                            
                            <div className="mb-3">
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fs-12 fw-medium text-dark">Course Type</div>
                                <span className="badge badge-soft-info">{course.type || 'N/A'}</span>
                              </div>

                              {course.status && (
                                <div className="d-flex justify-content-between">
                                  <div className="fs-12 fw-medium text-dark">Status</div>
                                  <div>
                                    {course.status === 'ACTIVE' ? (
                                      <span className="badge badge-soft-success d-flex align-items-center">
                                        <i className="ti ti-circle-filled fs-8 me-1"></i> Active
                                      </span>
                                    ) : (
                                      <span className="badge badge-soft-danger d-flex align-items-center">
                                        <i className="ti ti-circle-filled fs-8 me-1"></i> Inactive
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Subjects info if available */}
                            {course.subjects && (
                              <div className="subject-summary mb-3 p-2 bg-light-subtle rounded-2 border-start border-3 border-primary">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="fs-12 fw-medium text-dark">Subjects ({course.subjects.length})</div>
                                  <Link 
                                    to={`${all_routes.courseAcademicWiz}`} 
                                    className="fs-11 text-primary"
                                    title="Manage subjects"
                                  >
                                    Manage
                                  </Link>
                                </div>
                                {course.subjects.length > 0 ? (
                                  <div className="subject-pills">
                                    {course.subjects.slice(0, 3).map((subject: any, idx: number) => (
                                      <span key={idx} className="badge badge-soft-secondary me-1 mb-1">
                                        {subject.title}
                                      </span>
                                    ))}
                                    {course.subjects.length > 3 && (
                                      <span className="badge badge-soft-primary">
                                        +{course.subjects.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-muted fs-12">No subjects assigned</div>
                                )}
                              </div>
                            )}
                            
                            <div className="d-flex justify-content-end mt-3 gap-2 flex-wrap">
                              {course?.isCommercial && (
                                <>
                                  <button 
                                    hidden={!hasAcademicMgntDelete()}
                                    className="btn btn-sm btn-outline-primary" 
                                    onClick={() => onAddSubscription(course?.id)}
                                  >
                                    <i className="ti ti-briefcase me-1" /> Add Subscription
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-info" 
                                    onClick={() => onViewReviews(course)}
                                    title="View Reviews"
                                  >
                                    <i className="ti ti-star me-1" /> Reviews
                                  </button>
                                </>
                              )}
                              <Link
                                hidden={!hasAcademicMgntEdit()}
                                to="#"
                                className="btn btn-sm btn-outline-primary"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#edit_course"
                                onClick={() => { setEditId(course?.id) }}
                                title="Edit Course"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <Link
                                hidden={!hasAcademicMgntDelete()}
                                to="#"
                                className="btn btn-sm btn-outline-danger"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#delete_modal"
                                onClick={() => setDeleteItem({
                                  id: course.id,
                                  message: `Are you sure you want to delete ${course.title}? This can't be undone.`
                                })}
                                title="Delete Course"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {courses.length === 0 && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-search-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No courses found</h5>
                          <p className="text-muted">No courses match your current filter criteria.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Course */}

      {renderAddCourse()}

      {/* /Add Course */}
      {/* Edit Course */}

      {renderEditCourse()}

      {/* /Edit Course */}

      {/* User Reviews Modal */}
      <CourseReviews
        show={showReviewsModal}
        onClose={() => {
          setShowReviewsModal(false);
          setSelectedCourseForReviews(null);
        }}
        course={selectedCourseForReviews}
        onError={(message, heading) => {
          setToastSettings((prev) => ({
            ...prev,
            show: true,
            message,
            heading,
            type: "ERROR",
          }));
        }}
      />
      {/* /User Reviews Modal */}
    </>
  );
};

export default Courses;